// ---------------------------------------------------------------------------
// Resilient axios instance for WhatsApp Bot UI.
//
// Why: the bot backend may go through brief blips (5–30 sec) for legitimate
// reasons - hot-reload after a deploy, the Node.js Baileys gateway re-running
// a session after a phone reconnect, the platform proxy routing during a
// scale event. None of these should ever surface as a red error banner to
// the user. This wrapper transparently retries any failing wabot request
// with exponential backoff before giving up, and treats 404 / 5xx / network
// errors as retryable.
//
// Behaviour:
//   - GET requests: retried up to 5 times (32s total max wait)
//   - POST/PUT/DELETE: retried up to 2 times (idempotent ones recover, the
//     rare non-idempotent ones won't double-fire because the gateway uses
//     unique IDs and our trigger/campaign endpoints have idempotency keys)
//   - 4xx other than 404/408/429 → fail immediately (legitimate user error,
//     no point retrying)
// ---------------------------------------------------------------------------

import axios from 'axios';
import { WABOT_BACKEND_ORIGIN } from './wabotApi';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Exponential backoff: 500ms, 1s, 2s, 4s, 8s, 16s — capped at 16s
const backoffMs = (attempt) => Math.min(16000, 500 * Math.pow(2, attempt));

const isRetryableStatus = (s) => s === 404 || s === 408 || s === 429 || s >= 500;
const isRetryableError = (err) => {
  if (!err) return false;
  // Network failure / aborted → retry
  if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || err.message === 'Network Error') return true;
  if (!err.response) return true; // no response means transport failure
  return isRetryableStatus(err.response.status);
};

const wabotAxios = axios.create({
  baseURL: WABOT_BACKEND_ORIGIN,
  timeout: 25000,
});

wabotAxios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const cfg = error.config || {};
    if (cfg.__no_retry) return Promise.reject(error);
    const method = (cfg.method || 'get').toLowerCase();
    const isRead = method === 'get' || method === 'head';
    const maxRetries = isRead ? 5 : 2;
    cfg.__retry_count = (cfg.__retry_count || 0);
    if (cfg.__retry_count >= maxRetries || !isRetryableError(error)) {
      return Promise.reject(error);
    }
    cfg.__retry_count += 1;
    const wait = backoffMs(cfg.__retry_count - 1);
    if (process.env.NODE_ENV !== 'production') {
      // Quiet console — only logs during retries, never on success
      console.warn(`[wabot] retry ${cfg.__retry_count}/${maxRetries} in ${wait}ms for ${method.toUpperCase()} ${cfg.url}`);
    }
    await sleep(wait);
    return wabotAxios.request(cfg);
  }
);

export default wabotAxios;
